import { useCallback, useEffect, useRef, useState } from "react";

// Autosave a text draft: 1.5s after typing stops, and immediately on blur
// (call `flush`). `onSave(text)` must resolve true only on a real 200.
// Warns on page leave while there is unsaved text.
export function useAutosave({ draft, savedValue, onSave, delay = 1500 }) {
  const [status, setStatus] = useState("idle"); // idle | saving | saved | error
  const timer = useRef(null);
  const lastSaved = useRef((savedValue || "").trim());
  const inFlight = useRef(null);

  useEffect(() => { lastSaved.current = (savedValue || "").trim(); }, [savedValue]);

  const clean = (draft || "").trim();
  const dirty = clean !== lastSaved.current;

  const flush = useCallback(async () => {
    window.clearTimeout(timer.current);
    const text = (draft || "").trim();
    if (text === lastSaved.current) return true;
    if (inFlight.current === text) return true;
    inFlight.current = text;
    setStatus("saving");
    const ok = await onSave(text);
    inFlight.current = null;
    if (ok) lastSaved.current = text;
    setStatus(ok ? "saved" : "error");
    return ok;
  }, [draft, onSave]);

  useEffect(() => {
    if (!dirty) return undefined;
    if (status === "saved" || status === "error") setStatus("idle");
    timer.current = window.setTimeout(flush, delay);
    return () => window.clearTimeout(timer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft]);

  useEffect(() => {
    if (!dirty && status !== "saving") return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty, status]);

  return { status, dirty, flush };
}
